/**
 * Board Report Template Form
 * Board meeting preparation and CxO reports
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2, Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const BoardReportForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);

  const getEmptyForm = () => ({
    company_name: '', meeting_date: new Date().toISOString().split('T')[0], meeting_type: 'Regular', 
    prepared_by: user?.email || '', created_by_email: user?.email || '', version: '1.0',
    agenda_items: [{ item_number: '1', topic: '', presenter: '', duration: '', type: 'Discussion', materials: '', notes: '' }],
    ceo_report: '', cfo_report: '', coo_report: '', cto_report: '',
    financial_highlights: [{ metric: '', value: '', change: '', notes: '' }],
    operational_highlights: [''], key_metrics: [{ name: '', current_value: '', target_value: '', status: 'On Track' }],
    strategic_updates: [''], risks_opportunities: [{ type: 'Risk', description: '', mitigation: '', owner: '' }],
    resolutions_required: [''], compliance_updates: [''],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_type: 'Regular',
    prepared_by: user?.email || 'Corporate Secretary',
    created_by_email: user?.email || '',
    version: '1.0',
    agenda_items: [
      { item_number: '1', topic: 'Call to Order & Quorum', presenter: 'Chairman', duration: '5 min', type: 'Information', materials: '', notes: '' },
      { item_number: '2', topic: 'Approval of Previous Minutes', presenter: 'Secretary', duration: '5 min', type: 'Decision', materials: 'Previous meeting minutes', notes: '' },
      { item_number: '3', topic: 'CEO Report', presenter: 'CEO', duration: '15 min', type: 'Information', materials: 'CEO Report Deck', notes: '' },
      { item_number: '4', topic: 'Financial Review', presenter: 'CFO', duration: '20 min', type: 'Discussion', materials: 'Q1 Financials', notes: 'Q1 results and forecast' },
      { item_number: '5', topic: 'Strategic Initiative Update', presenter: 'COO', duration: '15 min', type: 'Discussion', materials: 'Operations Report', notes: '' },
      { item_number: '6', topic: 'Technology Roadmap', presenter: 'CTO', duration: '15 min', type: 'Information', materials: 'Tech Roadmap', notes: 'AI initiative update' },
      { item_number: '7', topic: 'Budget Approval for Q2', presenter: 'CFO', duration: '10 min', type: 'Decision', materials: 'Q2 Budget Proposal', notes: 'Board vote required' },
      { item_number: '8', topic: 'Any Other Business', presenter: 'Chairman', duration: '10 min', type: 'Discussion', materials: '', notes: '' }
    ],
    ceo_report: 'Q1 exceeded expectations with 18% revenue growth YoY. Key wins include 3 enterprise contracts worth $2M ARR combined. Team expanded to 120 employees. Focus for Q2: accelerate product development and expand to APAC market.',
    cfo_report: 'Revenue: $12.5M (8% above target). EBITDA margin improved to 22%. Cash position: $8M. Burn rate sustainable for 18+ months. Requesting Q2 budget increase of $500K for APAC expansion.',
    coo_report: 'Operations scaled to support 3x user growth. Customer satisfaction at 92%. Implemented new support ticketing system reducing response time by 40%. Key challenge: hiring in competitive market.',
    cto_report: 'Platform V2 on track for Q2 release. AI features in beta with positive feedback. Security audit completed - no critical issues. Technical debt reduction initiative showing 30% improvement in deployment frequency.',
    financial_highlights: [
      { metric: 'Revenue', value: '$12.5M', change: '+18% YoY', notes: 'Above target' },
      { metric: 'Gross Margin', value: '72%', change: '+2%', notes: '' },
      { metric: 'EBITDA', value: '$2.75M', change: '+25%', notes: '' },
      { metric: 'ARR', value: '$48M', change: '+22%', notes: 'Strong enterprise growth' },
      { metric: 'Cash Position', value: '$8M', change: '-$2M', notes: 'Planned investment' }
    ],
    operational_highlights: [
      'Customer count grew to 2,500 (+15%)',
      'Employee headcount: 120 (net +15 in Q1)',
      'NPS improved to 65 (from 58)',
      'Platform uptime: 99.95%'
    ],
    key_metrics: [
      { name: 'Monthly Recurring Revenue', current_value: '$4.0M', target_value: '$4.2M', status: 'At Risk' },
      { name: 'Customer Acquisition Cost', current_value: '$450', target_value: '$500', status: 'On Track' },
      { name: 'Customer Lifetime Value', current_value: '$12,000', target_value: '$10,000', status: 'On Track' },
      { name: 'Churn Rate', current_value: '2.1%', target_value: '2.0%', status: 'At Risk' }
    ],
    strategic_updates: [
      'APAC expansion: Singapore office opening Q2, Japan market research completed',
      'AI Product: Beta launch successful, 85% user satisfaction, full launch planned Q2',
      'Enterprise sales: Pipeline of $5M in opportunities for H1'
    ],
    risks_opportunities: [
      { type: 'Risk', description: 'Key competitor raised $50M funding', mitigation: 'Accelerate product roadmap, focus on differentiation', owner: 'CEO' },
      { type: 'Risk', description: 'Engineering talent shortage', mitigation: 'Remote hiring program, improved compensation', owner: 'COO' },
      { type: 'Opportunity', description: 'Partnership with major cloud provider', mitigation: 'In late-stage discussions, potential 2x distribution', owner: 'CEO' },
      { type: 'Opportunity', description: 'Government contract opportunity', mitigation: 'RFP submitted, decision expected Q2', owner: 'CFO' }
    ],
    resolutions_required: [
      'Approve Q2 operating budget of $4.2M',
      'Authorize APAC subsidiary formation',
      'Approve stock option grant for new executives'
    ],
    compliance_updates: [
      'SOC 2 Type II audit completed successfully',
      'GDPR compliance maintained, no incidents',
      'Annual security assessment scheduled for Q2'
    ],
    rai_assignments: [
      { task: 'Finalize APAC subsidiary paperwork', responsible: 'Legal Counsel', accountable: 'CFO', informed: 'Board', due_date: '2025-02-15', status: 'In Progress' }
    ],
    followups: [
      { item: 'Distribute approved meeting minutes', owner: 'Corporate Secretary', due_date: '2025-01-20', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.company_name ? `Board Report - ${dataToSave.company_name}` : "Board Report Draft",
        template_type: "Board Report",
        template_id: "board-report",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Update created_by_email when user loads
  useEffect(() => {
    if (user?.email && !formData.created_by_email) {
      setFormData(prev => ({ ...prev, created_by_email: user.email, prepared_by: user.email }));
    }
  }, [user]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        // Use refs to get latest values
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "board-report", "Board Report", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("board-report", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      // Always include logged-in user's email for "Created By"
      const dataWithUser = { ...formData, created_by_email: user?.email || '' };
      const response = await axios.post(`${API}/generate/board-report`, dataWithUser, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Board_Report_${formData.company_name.replace(/\s+/g, '_') || 'Company'}_${formData.meeting_date}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Board Report",
        formData,
        settings: activeSettings || {},
        templateType: "Board Report",
        templateId: "board-report",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const meetingTypes = ['Regular', 'Special', 'Annual', 'Emergency'];
  const agendaTypes = ['Decision', 'Discussion', 'Information'];
  const statuses = ['On Track', 'At Risk', 'Off Track'];
  const riskTypes = ['Risk', 'Opportunity'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Board Report</h1><p className="text-sm text-gray-500">Board meeting preparation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Meeting Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="TechCorp Inc." /></div>
              <div><Label>Meeting Date</Label><Input type="date" value={formData.meeting_date} onChange={(e) => setFormData({...formData, meeting_date: e.target.value})} /></div>
              <div><Label>Meeting Type</Label>
                <Select value={formData.meeting_type} onValueChange={(v) => setFormData({...formData, meeting_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{meetingTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>CEO Report</Label><Textarea value={formData.ceo_report} onChange={(e) => setFormData({...formData, ceo_report: e.target.value})} placeholder="CEO's update to the board..." rows={4} /></div>
              <div><Label>CFO Report</Label><Textarea value={formData.cfo_report} onChange={(e) => setFormData({...formData, cfo_report: e.target.value})} placeholder="CFO's financial update..." rows={4} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>COO Report</Label><Textarea value={formData.coo_report} onChange={(e) => setFormData({...formData, coo_report: e.target.value})} placeholder="COO's operations update..." rows={4} /></div>
              <div><Label>CTO Report</Label><Textarea value={formData.cto_report} onChange={(e) => setFormData({...formData, cto_report: e.target.value})} placeholder="CTO's technology update..." rows={4} /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Resolutions Required</Label>
              {formData.resolutions_required.map((res, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <Input value={res} onChange={(e) => { const r = [...formData.resolutions_required]; r[idx] = e.target.value; setFormData({...formData, resolutions_required: r}); }} placeholder="Resolution requiring board approval" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, resolutions_required: formData.resolutions_required.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, resolutions_required: [...formData.resolutions_required, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <div>
              <Label className="font-semibold">Strategic Updates</Label>
              {formData.strategic_updates.map((update, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <Input value={update} onChange={(e) => { const u = [...formData.strategic_updates]; u[idx] = e.target.value; setFormData({...formData, strategic_updates: u}); }} placeholder="Strategic initiative update" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, strategic_updates: formData.strategic_updates.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, strategic_updates: [...formData.strategic_updates, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                  <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
                </Button>
                <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                  <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
                </Button>
                <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
                </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BoardReportForm;
